/**
 * CNC PM PWA - LINE Flex Message & Webhook Payload Generator
 * Implements LINE Messaging API Flex Message v2 Container Bubble,
 * Generic Webhook schema (Make/n8n/Zapier), and LINE Notify fallback.
 */

import type { LineAnomalyPayload } from '../../types';

export interface LineFlexMessage {
  type: 'flex';
  altText: string;
  contents: {
    type: 'bubble';
    size: 'mega';
    header: {
      type: 'box';
      layout: 'vertical';
      backgroundColor: string;
      paddingAll?: string;
      contents: Array<Record<string, any>>;
    };
    body: {
      type: 'box';
      layout: 'vertical';
      paddingAll?: string;
      spacing?: string;
      contents: Array<Record<string, any>>;
    };
    footer: {
      type: 'box';
      layout: 'vertical';
      paddingAll?: string;
      contents: Array<Record<string, any>>;
    };
  };
}

export interface GenericWebhookPayload {
  event: 'pm.inspection.anomaly_detected';
  eventId: string;
  timestamp: string;
  machine: {
    id: string;
    name: string;
    model?: string;
    serialNumber?: string;
    cumulativeHours: number;
  };
  inspection: {
    submissionId: string;
    frequency?: string;
    inspector: string;
    inspectorRole?: string;
    recordUrl: string;
    defectCount: number;
    totalTasks?: number;
    passedCount?: number;
  };
  defects: Array<{
    taskIndex: number;
    taskName: string;
    type: string;
    measuredValue: string | number;
    unit?: string;
    criteria: string;
    severity?: 'CRITICAL' | 'MAJOR' | 'WARNING';
    operatorNotes?: string;
    photoAttachment?: string | null;
  }>;
}

export interface GasRelayPayload {
  action: 'relay_line_alert';
  lineChannelAccessToken: string;
  messagePayload: {
    to: string;
    messages: LineFlexMessage[];
  };
}

/**
 * Builds a rich LINE Flex Message v2 Bubble container with crimson red header (#EF4444)
 * per survey_explorer_integrations/report.md § 3.2 and line-simulator.mjs
 */
export function buildLineFlexMessage(payload: LineAnomalyPayload): LineFlexMessage {
  const defectsBoxes = payload.defects.map((d) => {
    const boxContents: Array<Record<string, any>> = [
      {
        type: 'text',
        text: `[Item ${d.itemNo}] ${d.task}`,
        weight: 'bold',
        size: 'sm',
        color: '#1E293B',
        wrap: true,
      },
      {
        type: 'text',
        text: `Criteria: ${d.criteria}`,
        size: 'xs',
        color: '#64748B',
        wrap: true,
      },
      {
        type: 'text',
        text: `Measured: ${d.measuredValue} (STATUS: ${d.status})`,
        size: 'xs',
        color: '#EF4444',
        weight: 'bold',
      },
    ];

    if (d.notes && d.notes.trim()) {
      boxContents.push({
        type: 'text',
        text: `Notes: ${d.notes.trim()}`,
        size: 'xs',
        color: '#334155',
        wrap: true,
      });
    }

    return {
      type: 'box',
      layout: 'vertical',
      margin: 'md',
      contents: boxContents,
    };
  });

  return {
    type: 'flex',
    altText: `🚨 CRITICAL PM ANOMALY: ${payload.machineId} (${payload.machineName})`,
    contents: {
      type: 'bubble',
      size: 'mega',
      header: {
        type: 'box',
        layout: 'vertical',
        backgroundColor: '#EF4444',
        contents: [
          {
            type: 'text',
            text: '🚨 PM DEFECT / OUT-OF-TOLERANCE ALERT',
            color: '#FFFFFF',
            weight: 'bold',
            size: 'sm',
          },
        ],
      },
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'text',
            text: `${payload.machineId} — ${payload.machineName}`,
            weight: 'bold',
            size: 'md',
            color: '#0F172A',
          },
          {
            type: 'box',
            layout: 'vertical',
            margin: 'sm',
            contents: [
              {
                type: 'text',
                text: `Inspector: ${payload.inspectorName}`,
                size: 'xs',
                color: '#475569',
              },
              {
                type: 'text',
                text: `Date/Time: ${payload.inspectionDate}`,
                size: 'xs',
                color: '#475569',
              },
              {
                type: 'text',
                text: `Running Hours: ${payload.operatingHours} h`,
                size: 'xs',
                color: '#475569',
              },
            ],
          },
          {
            type: 'separator',
            margin: 'md',
          },
          {
            type: 'text',
            text: 'Detected Faults & Violations:',
            weight: 'bold',
            size: 'xs',
            color: '#EF4444',
            margin: 'md',
          },
          ...defectsBoxes,
        ],
      },
      footer: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'button',
            action: {
              type: 'uri',
              label: 'View Record',
              uri: payload.recordUrl,
            },
            style: 'primary',
            color: '#0284C7',
          },
        ],
      },
    },
  };
}

/**
 * Builds generic automation webhook payload (Make / n8n / Zapier / Discord / Slack)
 * per survey_explorer_integrations/report.md § 3.3
 */
export function buildGenericWebhookPayload(
  payload: LineAnomalyPayload,
  extra?: {
    model?: string;
    serialNumber?: string;
    submissionId?: string;
    frequency?: string;
    inspectorRole?: string;
    totalTasks?: number;
    passedCount?: number;
  }
): GenericWebhookPayload {
  const eventId = `evt_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  const submissionId = extra?.submissionId || `sub_${Date.now()}`;

  return {
    event: 'pm.inspection.anomaly_detected',
    eventId,
    timestamp: new Date().toISOString(),
    machine: {
      id: payload.machineId,
      name: payload.machineName,
      model: extra?.model,
      serialNumber: extra?.serialNumber,
      cumulativeHours: payload.operatingHours,
    },
    inspection: {
      submissionId,
      frequency: extra?.frequency,
      inspector: payload.inspectorName,
      inspectorRole: extra?.inspectorRole,
      recordUrl: payload.recordUrl,
      defectCount: payload.defects.length,
      totalTasks: extra?.totalTasks,
      passedCount: extra?.passedCount,
    },
    defects: payload.defects.map((d) => ({
      taskIndex: d.itemNo,
      taskName: d.task,
      type: typeof d.measuredValue === 'number' ? 'GAUGE_OUT_OF_TOLERANCE' : 'DISCRETE_NG',
      measuredValue: d.measuredValue,
      criteria: d.criteria,
      severity: 'CRITICAL',
      operatorNotes: d.notes,
      photoAttachment: null,
    })),
  };
}

/**
 * Builds plain text message for LINE Notify API fallback (https://notify-api.line.me/api/notify)
 * per survey_explorer_integrations/report.md § 3.4
 */
export function buildLineNotifyMessage(payload: LineAnomalyPayload): string {
  const defectLines = payload.defects.map((d, index) => {
    let line = `❌ ${index + 1}. [Item ${d.itemNo}] ${d.task}\n   Measured: ${d.measuredValue} (Standard: ${d.criteria})`;
    if (d.notes && d.notes.trim()) {
      line += `\n   Note: ${d.notes.trim()}`;
    }
    return line;
  });

  return [
    `🚨 [CNC PM ANOMALY ALERT]`,
    `Machine: ${payload.machineName} (${payload.machineId})`,
    `Inspector: ${payload.inspectorName}`,
    `Date/Time: ${payload.inspectionDate}`,
    `Operating Hours: ${payload.operatingHours} h`,
    `Total Defects: ${payload.defects.length} Items`,
    ``,
    ...defectLines,
    ``,
    `🔗 View Details: ${payload.recordUrl}`,
  ].join('\n');
}

/**
 * Builds Google Apps Script CORS relay payload
 * per survey_explorer_integrations/report.md § 3.5 & 4.1
 */
export function buildGasRelayPayload(
  lineChannelAccessToken: string,
  toTargetId: string,
  flexMessage: LineFlexMessage
): GasRelayPayload {
  return {
    action: 'relay_line_alert',
    lineChannelAccessToken,
    messagePayload: {
      to: toTargetId,
      messages: [flexMessage],
    },
  };
}
